import React, {
    useEffect,
    useState,
} from "react";

import {
    FiAlertTriangle,
    FiLoader,
    FiRotateCcw,
    FiX,
} from "react-icons/fi";

import "../PaymentOut.css";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(value)
    );


const getSupplierName = (
    payment
) =>
    payment
        ?.supplierSnapshot
        ?.supplierName ||
    payment
        ?.supplierSnapshot
        ?.companyName ||
    payment
        ?.supplier
        ?.supplierName ||
    payment
        ?.supplier
        ?.name ||
    "Supplier";


const getErrorMessage = (
    error,
    fallback
) => {
    if (
        typeof error ===
        "string"
    ) {
        return error;
    }

    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
    );
};


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const ReversePaymentOutDialog = ({
    open = false,

    paymentOut = null,

    loading = false,

    onClose,

    onConfirm,
}) => {
    const [
        reversalReason,
        setReversalReason,
    ] = useState("");

    const [
        localError,
        setLocalError,
    ] = useState("");


    /*
    |--------------------------------------------------------------------------
    | RESET
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (!open) {
                return;
            }

            setReversalReason(
                ""
            );

            setLocalError(
                ""
            );
        },
        [
            open,
            paymentOut?._id,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | CLOSE
    |--------------------------------------------------------------------------
    */

    const handleClose =
        () => {
            if (loading) {
                return;
            }

            setLocalError(
                ""
            );

            setReversalReason(
                ""
            );

            onClose?.();
        };


    /*
    |--------------------------------------------------------------------------
    | BACKDROP
    |--------------------------------------------------------------------------
    */

    const handleBackdropClick =
        (
            event
        ) => {
            if (
                event.target ===
                event.currentTarget
            ) {
                handleClose();
            }
        };


    /*
    |--------------------------------------------------------------------------
    | SUBMIT
    |--------------------------------------------------------------------------
    */

    const handleSubmit =
        async () => {
            const normalizedReason =
                String(
                    reversalReason ||
                    ""
                ).trim();

            if (
                !normalizedReason
            ) {
                setLocalError(
                    "Reversal reason is required."
                );

                return;
            }

            if (
                normalizedReason.length <
                3
            ) {
                setLocalError(
                    "Please enter a valid reversal reason."
                );

                return;
            }

            if (
                !paymentOut?._id
            ) {
                setLocalError(
                    "Payment Out ID is missing."
                );

                return;
            }

            try {
                setLocalError(
                    ""
                );

                await onConfirm?.(
                    normalizedReason
                );
            } catch (
                error
            ) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to reverse supplier payment."
                    )
                );
            }
        };


    /*
    |--------------------------------------------------------------------------
    | DO NOT RENDER
    |--------------------------------------------------------------------------
    */

    if (
        !open ||
        !paymentOut
    ) {
        return null;
    }


    /*
    |--------------------------------------------------------------------------
    | VALUES
    |--------------------------------------------------------------------------
    */

    const paymentNumber =
        paymentOut
            ?.paymentNumber ||
        paymentOut
            ?.documentNumber ||
        "-";

    const amount =
        roundMoney(
            paymentOut?.amount
        );

    const allocatedAmount =
        roundMoney(
            paymentOut
                ?.allocatedAmount
        );

    const unallocatedAmount =
        roundMoney(
            paymentOut
                ?.unallocatedAmount
        );


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div
            className="payment-out-reverse-overlay"
            onMouseDown={
                handleBackdropClick
            }
        >
            <div
                className="payment-out-reverse-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="payment-out-reverse-title"
                onMouseDown={(
                    event
                ) =>
                    event.stopPropagation()
                }
            >
                {/* HEADER */}

                <header className="payment-out-reverse-header">
                    <div className="payment-out-reverse-header-content">
                        <span className="payment-out-reverse-header-icon">
                            <FiRotateCcw />
                        </span>

                        <div>
                            <small>
                                SUPPLIER PAYMENT
                            </small>

                            <h2 id="payment-out-reverse-title">
                                Reverse Payment
                            </h2>

                            <p>
                                Reverse this payment and restore
                                the affected purchase bill balances.
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="payment-out-reverse-close"
                        disabled={
                            loading
                        }
                        onClick={
                            handleClose
                        }
                    >
                        <FiX />
                    </button>
                </header>


                {/* BODY */}

                <div className="payment-out-reverse-body">

                    {/* WARNING */}

                    <div className="payment-out-reverse-warning">
                        <span>
                            <FiAlertTriangle />
                        </span>

                        <div>
                            <strong>
                                This action affects accounting records
                            </strong>

                            <p>
                                Reversing this payment will remove its
                                active allocations from purchase bills
                                and restore the corresponding supplier
                                payable balances.
                            </p>
                        </div>
                    </div>


                    {/* PAYMENT DETAILS */}

                    <section className="payment-out-reverse-payment-card">
                        <div className="payment-out-reverse-payment-header">
                            <div>
                                <span>
                                    Payment
                                </span>

                                <strong>
                                    {paymentNumber}
                                </strong>
                            </div>

                            <div className="payment-out-reverse-payment-amount">
                                {formatCurrency(
                                    amount
                                )}
                            </div>
                        </div>

                        <div className="payment-out-reverse-info-grid">
                            <div>
                                <span>
                                    Supplier
                                </span>

                                <strong>
                                    {getSupplierName(
                                        paymentOut
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Allocated
                                </span>

                                <strong>
                                    {formatCurrency(
                                        allocatedAmount
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Supplier Advance
                                </span>

                                <strong>
                                    {formatCurrency(
                                        unallocatedAmount
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Status
                                </span>

                                <strong>
                                    {String(
                                        paymentOut
                                            ?.status ||
                                        "-"
                                    )
                                        .replace(
                                            /_/g,
                                            " "
                                        )}
                                </strong>
                            </div>
                        </div>
                    </section>


                    {/* ERROR */}

                    {localError && (
                        <div className="payment-out-reverse-error">
                            <FiAlertTriangle />

                            <span>
                                {localError}
                            </span>
                        </div>
                    )}


                    {/* REASON */}

                    <div className="payment-out-reverse-field">
                        <label>
                            Reversal Reason

                            <span>
                                *
                            </span>
                        </label>

                        <textarea
                            rows="4"
                            maxLength="500"
                            value={
                                reversalReason
                            }
                            disabled={
                                loading
                            }
                            placeholder="Enter the reason for reversing this payment..."
                            onChange={(
                                event
                            ) => {
                                setReversalReason(
                                    event
                                        .target
                                        .value
                                );

                                if (
                                    localError
                                ) {
                                    setLocalError(
                                        ""
                                    );
                                }
                            }}
                        />

                        <div className="payment-out-reverse-field-footer">
                            <span>
                                Required for audit history.
                            </span>

                            <span>
                                {
                                    reversalReason
                                        .length
                                } / 500
                            </span>
                        </div>
                    </div>
                </div>


                {/* FOOTER */}

                <footer className="payment-out-reverse-footer">
                    <button
                        type="button"
                        className="payment-out-button"
                        disabled={
                            loading
                        }
                        onClick={
                            handleClose
                        }
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="payment-out-button payment-out-button-danger"
                        disabled={
                            loading ||
                            !reversalReason
                                .trim()
                        }
                        onClick={
                            handleSubmit
                        }
                    >
                        {loading ? (
                            <>
                                <FiLoader className="payment-out-spinner" />

                                Reversing...
                            </>
                        ) : (
                            <>
                                <FiRotateCcw />

                                Confirm Reversal
                            </>
                        )}
                    </button>
                </footer>
            </div>
        </div>
    );
};


export default ReversePaymentOutDialog;